import classNames from 'classnames';
import { useEffect, useState } from 'react';
import React from 'react';
import { Autoplay, Thumbs } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types';
import variables from '@/components/modules/header-carousel/header-carousel.variables';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import HeaderCarouselItem, {
  HeaderCarouselItemProps,
} from './header-carousel-item/header-carousel-item';
import HeaderCarouselNavigation from './header-carousel-navigation/header-carousel-navigation';
import HeaderCarouselPreview from './header-carousel-preview/header-carousel-preview';
import HeaderCarouselSearch from './header-carousel-search/header-carousel-search';
import styles from './header-carousel.module.scss';

export interface HeaderCarouselProps extends ModuleWrapperProps {
  /** Search settings for the search bar */
  searchSettings?: Content.Components['HeaderCarouselModuleDto']['searchSettings'];
  /** Indicates whether to show the search bar */
  showSearch?: Content.Components['HeaderCarouselModuleDto']['showSearch'];
  /** The slides to show */
  slides: Content.Components['HeaderCarouselModuleDto']['slides'];
}

const AUTOPLAY_DELAY = 5000;

const SWIPER_CONFIGURATION: SwiperOptions = {
  autoplay: { delay: AUTOPLAY_DELAY, disableOnInteraction: true },
  slidesPerGroup: 1,
  slidesPerView: 1,
  spaceBetween: 0,
  speed: 500,
};

const HeaderCarousel = ({
  searchSettings,
  showSearch = true,
  slides = [],
  ...rest
}: HeaderCarouselProps) => {
  const isLaptop = useWindowSize('laptop');
  const [previewSwiper, setPreviewSwiper] = useState<SwiperClass>();
  const [thumbs, setThumbs] = useState<HeaderCarouselItemProps[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const { hasTransparentNavigation } = usePageDetailsContext();
  const variant = variables.variant;

  useEffect(() => {
    if (!slides.length) return;

    const thumbsArray = [...slides];
    const firstSlide = thumbsArray.shift();

    if (firstSlide) {
      thumbsArray.push(firstSlide);
    }

    setThumbs(thumbsArray);
  }, [slides]);

  return (
    <React.Fragment>
      <ModuleWrapper
        {...rest}
        className={classNames(styles.component, {
          [styles.hasSearchBar]: showSearch,
          [styles.hasTransparentNavigation]: hasTransparentNavigation,
        })}
        padding="none"
      >
        <div className={styles.wrapper}>
          <Swiper
            {...SWIPER_CONFIGURATION}
            className={styles.swiper}
            loop={slides.length > 1}
            modules={[Autoplay, Thumbs]}
            onSlideChangeTransitionStart={slide => {
              setCurrentIndex(slide.realIndex);
            }}
            thumbs={{
              swiper:
                previewSwiper && !previewSwiper.destroyed
                  ? previewSwiper
                  : undefined,
            }}
          >
            {slides.map((slide, slideIndex) => (
              <SwiperSlide key={slideIndex} className={styles.slide}>
                <HeaderCarouselItem
                  {...slide}
                  currentIndex={currentIndex}
                  hasSearchBar={showSearch}
                  isPlaying={currentIndex === slideIndex && isPlaying}
                  isPrimary={slideIndex === 0}
                  pageCount={slides.length}
                  variant={variant}
                />
              </SwiperSlide>
            ))}

            {slides.length > 1 && (
              <div className={styles.controls}>
                <Container className={styles.container}>
                  <HeaderCarouselNavigation
                    currentIndex={currentIndex}
                    isPlaying={isPlaying}
                    onPause={() => setIsPlaying(false)}
                    onPlay={() => setIsPlaying(true)}
                    pageCount={slides.length}
                    previewSwiper={previewSwiper}
                    timerDuration={AUTOPLAY_DELAY}
                    variant={variant}
                  />

                  {variant === 'primary' && isLaptop && (
                    <HeaderCarouselPreview
                      configuration={SWIPER_CONFIGURATION}
                      currentIndex={currentIndex}
                      isPlaying={isPlaying}
                      items={thumbs}
                      onSwiper={swiperInstance => {
                        setPreviewSwiper(swiperInstance);
                      }}
                      timerDuration={AUTOPLAY_DELAY}
                    />
                  )}
                </Container>
              </div>
            )}
          </Swiper>
        </div>
      </ModuleWrapper>

      {searchSettings && (
        <HeaderCarouselSearch searchSettings={searchSettings} />
      )}
    </React.Fragment>
  );
};

HeaderCarousel.displayName = 'HeaderCarousel';

export default HeaderCarousel;
